import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  SignOut, Buildings, User, Phone, Envelope, MapPin, CalendarBlank, FileText, Receipt,
  Package, Sparkle, ArrowRight, Spinner, Receipt as ReceiptIcon,
  CloudArrowUp, FileImage, FilePdf, FileVideo, PaperPlaneTilt, CheckCircle, Warning,
  Wrench, ShieldWarning, ChatsCircle,
} from "@phosphor-icons/react";
import api, {
  fetchCustomers, fetchCustomerPersons, fetchCustomerQuotes,
  fetchMyUploads, fetchMyUploadsReps, shareMyUpload,
  fetchAiGreeting,
} from "@/lib/api";
import { useT, useI18n } from "@/lib/i18n";
import { useAuthenticatedDownload } from "@/lib/useAuthenticatedDownload";
import { HelpPill } from "@/components/HeaderBrand";
import AddCustomerModal from "@/components/AddCustomerModal";
import { useUnreadBadge } from "@/lib/conv/notificationStore";

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch { return d; }
}
function fmtMoney(v, ccy = "USD") {
  if (v == null || v === "" || isNaN(Number(v))) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(Number(v));
  } catch { return `$${Number(v).toFixed(0)}`; }
}

export default function MyAccountPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useI18n();
  const { totalUnread, mentionUnread } = useUnreadBadge();
  const [customer, setCustomer] = useState(null);
  const [persons, setPersons] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openQuoteId, setOpenQuoteId] = useState(null);
  const [showSelfCreate, setShowSelfCreate] = useState(false);
  const [serviceTechCan, setServiceTechCan] = useState(false);
  // iter150AZ.60 — authenticated download for the Warranty Policy PDF.
  const { downloading: warrantyDocDownloading, download: downloadWarrantyDoc } =
    useAuthenticatedDownload();
  // iter150V — fleet rollup (compressors + upcoming PMs). Re-uses the
  // greeting endpoint so we don't duplicate the MySQL joins.
  const [rollup, setRollup] = useState(null);

  // Probe Service-Tech visibility — only show the shortcut if the customer
  // owns at least one compressor.
  useEffect(() => {
    let cancelled = false;
    api.get("/me/can-service-tech")
      .then((r) => { if (!cancelled) setServiceTechCan(!!r.data?.can); })
      .catch(() => { if (!cancelled) setServiceTechCan(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setErr("");
      try {
        // The middleware scopes /api/customers to their own record for customer-role.
        const list = await fetchCustomers({ per_page: 1 });
        const c = (list.items || [])[0];
        if (!c) { setErr("No company record linked to your account."); setLoading(false); return; }
        if (cancelled) return;
        setCustomer(c);
        const [p, q] = await Promise.allSettled([
          fetchCustomerPersons(c.CustomerID),
          fetchCustomerQuotes(c.CustomerID, 50),
        ]);
        if (cancelled) return;
        if (p.status === "fulfilled") setPersons(p.value.persons || []);
        if (q.status === "fulfilled") setQuotes(q.value.quotes || []);
      } catch (ex) {
        setErr(ex?.response?.data?.detail || "Could not load account");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const currency = customer?.Currency || "USD";
  const billAddress = customer?.["Bill to Address"] || "";

  // Group quotes by year for the timeline rail
  const groupedQuotes = useMemo(() => {
    const map = new Map();
    for (const q of quotes) {
      const y = q.quote_date ? new Date(q.quote_date).getFullYear() : "—";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(q);
    }
    return Array.from(map.entries()).sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  }, [quotes]);

  const totalQuotes = quotes.length;
  const totalValue = quotes.reduce((s, q) => s + (Number(q.total) || 0), 0);
  const openAmount = quotes.filter(q => (q.status || "").toLowerCase() === "open").reduce((s, q) => s + (Number(q.total) || 0), 0);
  const openQuoteCount = quotes.filter(q => (q.status || "").toLowerCase() === "open").length;

  // iter150V — "End User" detection (DB has typo variants: "EndUser",
  // "EndUSer", "End User"). Normalised match: lowercase, strip non-alnum,
  // compare to "enduser".
  const isEndUser = useMemo(() => {
    const t = (customer?.Type || "").toLowerCase().replace(/[^a-z]/g, "");
    return t === "enduser";
  }, [customer?.Type]);

  // Fetch fleet rollup ONLY for End-User customers. Other customer types
  // (dealers, distributors) get the existing layout untouched.
  useEffect(() => {
    if (!user?.email || !isEndUser) { setRollup(null); return; }
    let cancelled = false;
    fetchAiGreeting(user.email, lang)
      .then(d => { if (!cancelled) setRollup(d?.stats || null); })
      .catch(() => { if (!cancelled) setRollup(null); });
    return () => { cancelled = true; };
  }, [user?.email, isEndUser, lang]);

  const handleRefreshQuotes = useCallback(() => {
    if (!customer) return;
    fetchCustomerQuotes(customer.CustomerID, 50).then(d => setQuotes(d.quotes || [])).catch(() => {});
  }, [customer]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">
      <Spinner size={20} className="animate-spin mr-2" /> {t("common.loading", "Loading your account…")}
    </div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="my-account-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Buildings size={20} weight="bold" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            {customer?.["Company Name"] || t("myaccount.title", "My Account")}
          </h1>
          <p className="text-[11px] text-slate-500 truncate" data-testid="my-account-signed-in-as">
            {t("myaccount.signed_in_as", "Signed in as")} {(() => {
              const company = (customer?.["Company Name"] || "").trim().toLowerCase();
              const name = (user?.name || "").trim();
              // Some `Persons_for_Customer.Name` rows are misfilled with the
              // company name (e.g. "One way auto"). When the name duplicates
              // the company that's already shown above, fall back to the email
              // so the line doesn't read "Signed in as <Company> · <Company>".
              if (name && name.toLowerCase() !== company) return name;
              return user?.email || "—";
            })()}
          </p>
        </div>
        <Link to="/us-class" data-testid="my-account-price-list-link"
          className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-blue-700 border border-slate-200 rounded-sm px-2 py-1.5">
          <Package size={13} weight="bold" /> Price List
        </Link>
        <HelpPill />
        <button
          type="button"
          data-testid="my-account-warranty-doc-download"
          disabled={warrantyDocDownloading}
          onClick={() => downloadWarrantyDoc(
            "/warranty/master-doc.pdf",
            "Warranty_Policy.pdf",
            { params: { download: 1 } },
          )}
          title={t("wmd.download_pdf_hint", "Download the full Warranty Policy as a PDF, including the Labor Coverage Policy section.")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 border border-violet-200 hover:bg-violet-50 rounded-sm px-2 py-1.5 disabled:opacity-60 disabled:cursor-wait">
          <FileText size={13} weight="bold" /> {warrantyDocDownloading
            ? t("wmd.preparing_pdf", "Preparing…")
            : t("myaccount.warranty_policy", "Warranty Policy")}
        </button>
        <button onClick={onLogout} data-testid="my-account-logout"
          className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-700" title={t("compressor_page.sign_out", "Sign out")}>
          <SignOut size={16} />
        </button>
        {user?.email && !String(user.email).endsWith("@local.guest") && (
          <button
            data-testid="my-account-logout-all"
            onClick={() => {
              if (window.confirm("Sign out of all devices?")) onLogoutAll?.();
            }}
            className="h-8 px-2 inline-flex items-center gap-1 text-[10px] font-semibold rounded-sm border border-slate-200 text-slate-500 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
            title={t("myaccount.logout_all_tip", "Sign out of every device / browser / tab signed in with this email")}
          >
            <SignOut size={11} />
            All devices
          </button>
        )}
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {err && (
          <div className="lg:col-span-12 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="my-account-error">
            <div>{err}</div>
            {/* When the customer has no linked record, surface the self-creation CTA. */}
            {err.toLowerCase().includes("no company") && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowSelfCreate(true)}
                  data-testid="my-account-create-profile-btn"
                  className="text-xs font-bold uppercase tracking-wider px-3 h-8 rounded-sm bg-blue-700 text-white hover:bg-blue-800"
                >
                  + {t("myaccount.create_my_profile", "Create my customer profile")}
                </button>
                {/* iter150AC (sg_36203cc895e84a) — alternative path: ask the
                    ACC admin team to link an EXISTING customer record. */}
                <Link
                  to="/request-access"
                  data-testid="my-account-request-access-btn"
                  className="text-xs font-bold uppercase tracking-wider px-3 h-8 inline-flex items-center rounded-sm bg-white border border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  {t("myaccount.request_access_btn", "Request access to existing account")}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* iter150V — End-User fleet rollup. Renders only for customers
            whose Customer.Type ≈ "End User" (case/punctuation-insensitive).
            Three drill-in tiles surface the live fleet shape so customers
            don't have to click around to see what's coming. */}
        {isEndUser && rollup && (
          <section className="lg:col-span-12" data-testid="my-account-rollup">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-sm p-4 md:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  {t("myaccount.rollup_title", "Your fleet at a glance")}
                </h2>
                {rollup.company && (
                  <span className="text-[11px] text-slate-500" data-testid="my-account-rollup-company">{rollup.company}</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Link
                  to="/compressors"
                  data-testid="my-account-rollup-compressors"
                  className="bg-white border border-slate-200 rounded-sm p-3 md:p-4 hover:border-blue-400 hover:shadow-sm transition group"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-1 flex items-center gap-1">
                    <Package size={11} weight="bold" />
                    {t("myaccount.rollup_compressors", "Compressors")}
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-['Chivo'] text-slate-900 group-hover:text-blue-700">{rollup.compressors || 0}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 group-hover:text-blue-600">
                    {t("myaccount.rollup_compressors_cta", "View fleet →")}
                  </div>
                </Link>
                <Link
                  to="/compressors?pm_due_within=30"
                  data-testid="my-account-rollup-pms"
                  className="bg-white border border-slate-200 rounded-sm p-3 md:p-4 hover:border-amber-400 hover:shadow-sm transition group"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-1 flex items-center gap-1">
                    <Wrench size={11} weight="bold" />
                    {t("myaccount.rollup_pms_30d", "PMs ≤30d")}
                  </div>
                  <div className={`text-2xl md:text-3xl font-bold font-['Chivo'] ${(rollup.upcoming_pms_30d || 0) > 0 ? "text-amber-700" : "text-slate-900"} group-hover:text-amber-700`}>
                    {rollup.upcoming_pms_30d || 0}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 group-hover:text-amber-600">
                    {t("myaccount.rollup_pms_30d_cta", "Plan service →")}
                  </div>
                </Link>
                <a
                  href="#quotes-list"
                  data-testid="my-account-rollup-open-quotes"
                  className="bg-white border border-slate-200 rounded-sm p-3 md:p-4 hover:border-emerald-400 hover:shadow-sm transition group"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-1 flex items-center gap-1">
                    <Receipt size={11} weight="bold" />
                    {t("myaccount.rollup_open_quotes", "Open quotes")}
                  </div>
                  <div className={`text-2xl md:text-3xl font-bold font-['Chivo'] ${openQuoteCount > 0 ? "text-emerald-700" : "text-slate-900"} group-hover:text-emerald-700`}>
                    {openQuoteCount}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 group-hover:text-emerald-600">
                    {openQuoteCount > 0 ? fmtMoney(openAmount, currency) : t("myaccount.rollup_open_quotes_none", "None pending")}
                  </div>
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Left rail: company + contacts */}
        <section className="lg:col-span-5 space-y-5">
          {/* Company card */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5" data-testid="my-account-company-card">
            <div className="flex items-start gap-2 mb-3">
              <div className="shrink-0 w-10 h-10 rounded-sm bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-700">
                <Buildings size={22} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Company</div>
                <div className="text-lg font-bold font-['Chivo'] text-slate-900 truncate">{customer?.["Company Name"] || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex gap-2 flex-wrap">
                  {customer?.Type && <span>{customer.Type}</span>}
                  {customer?.Segment && <span>• {customer.Segment}</span>}
                  {customer?.Currency && <span>• {customer.Currency}</span>}
                </div>
              </div>
            </div>
            {billAddress && (
              <div className="border-t border-slate-100 pt-3 mt-2 flex items-start gap-2 text-xs text-slate-700" data-testid="my-account-address">
                <MapPin size={13} weight="bold" className="text-slate-400 shrink-0 mt-0.5" />
                <div className="whitespace-pre-line">{billAddress}</div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-100">
              <Stat label={t("myaccount.stat_quotes", "Quotes")} value={totalQuotes} />
              <Stat label={t("myaccount.stat_lifetime", "Lifetime")} value={fmtMoney(totalValue, currency)} />
              <Stat label={t("myaccount.stat_open", "Open")} value={fmtMoney(openAmount, currency)} />
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-5" data-testid="my-account-contacts-card">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Contacts</h2>
            {persons.length === 0 ? (
              <div className="text-xs text-slate-500 py-3 text-center">{t("myaccount.no_contacts", "No contacts on file.")}</div>
            ) : (
              <ul className="space-y-2">
                {persons.map((p) => {
                  const email = p["Main Email"] || (p.Emails || "").split(/[,;\s]+/)[0] || "";
                  const phone = p.Telephone || p["Mobile Number"] || p.Phone || "";
                  return (
                    <li key={p.PFCID} className="flex items-start gap-3 border-t border-slate-100 pt-2 first:border-0 first:pt-0" data-testid={`contact-${p.PFCID}`}>
                      <div className="shrink-0 w-8 h-8 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500">
                        <User size={15} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{p.Name || "—"}</div>
                        <div className="flex gap-3 mt-0.5 flex-wrap">
                          {email && <a href={`mailto:${email}?subject=${encodeURIComponent(customer?.["Company Name"] || "US Air")}`} className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1 truncate max-w-[190px]"><Envelope size={11} /> {email}</a>}
                          {phone && <a href={`tel:${phone.replace(/\D/g, '')}`} className="text-[11px] text-slate-700 hover:text-blue-700 inline-flex items-center gap-1"><Phone size={11} /> {phone}</a>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* Right: my uploads + quotes timeline */}
        <section className="lg:col-span-7 space-y-4">
          <MyUploadsInbox />
          <div id="quotes-list" className="bg-white border border-slate-200 rounded-sm p-4 md:p-5" data-testid="my-account-quotes-card">
            <div className="flex items-center gap-2 mb-3">
              <ReceiptIcon size={16} weight="bold" className="text-blue-700" />
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 flex-1">{t("myaccount.quotes_invoices", "Quotes & Invoices")}</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{totalQuotes}</span>
            </div>
            {quotes.length === 0 ? (
              <div className="text-xs text-slate-500 py-6 text-center" data-testid="my-account-no-quotes">{t("myaccount.no_quotes", "No quotes yet.")}</div>
            ) : (
              <div className="space-y-4">
                {groupedQuotes.map(([year, rows]) => (
                  <div key={year}>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 sticky top-14 bg-white/90 backdrop-blur py-0.5">{year}</div>
                    <ul className="space-y-1.5">
                      {rows.map((q) => {
                        const status = (q.status || "").trim();
                        const cls =
                          status.toLowerCase() === "paid" ? "text-green-700 bg-green-50 border-green-200" :
                          status.toLowerCase() === "open" ? "text-amber-700 bg-amber-50 border-amber-200" :
                          status.toLowerCase() === "cancelled" ? "text-red-700 bg-red-50 border-red-200" :
                          "text-slate-600 bg-slate-50 border-slate-200";
                        return (
                          <li key={q.QuoteInvoiceHeadingID} data-testid={`quote-row-${q.QuoteInvoiceHeadingID}`}>
                            <button type="button" onClick={() => setOpenQuoteId(q.QuoteInvoiceHeadingID)}
                              className="w-full text-left border border-slate-200 rounded-sm px-3 py-2 hover:border-blue-400 hover:shadow-sm transition-all bg-white flex items-center gap-3">
                              <FileText size={16} weight="bold" className="text-slate-400 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-slate-900">{q.QuoteInvoiceNumber || q.QuoteInvoiceHeadingID}</span>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{q.Q_Type || "—"}</span>
                                  {status && <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}>{status}</span>}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                                  <CalendarBlank size={11} weight="bold" /> {fmtDate(q.quote_date)}
                                  {q.Shipment && <span className="truncate max-w-[180px]" title={q.Shipment}>• {q.Shipment}</span>}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-sm font-mono font-semibold text-slate-900">{fmtMoney(q.total, currency)}</div>
                                <div className="text-[10px] text-slate-400 flex items-center gap-0.5 justify-end">View <ArrowRight size={10} weight="bold" /></div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Shortcuts grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Shortcut
              icon={ChatsCircle}
              label={t("myaccount.shortcut_conversations", "Conversations")}
              sub={t("myaccount.shortcut_conversations_sub", "Chat with our team")}
              onClick={() => navigate("/conversations-v2")}
              testId="shortcut-conversations"
              badge={
                mentionUnread > 0 ? (
                  <span
                    data-testid="shortcut-conversations-mention-dot"
                    aria-label={`${mentionUnread} unread mention${mentionUnread === 1 ? "" : "s"}`}
                    className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-rose-500 ring-2 ring-white animate-conv-breathe"
                  />
                ) : totalUnread > 0 ? (
                  <span
                    data-testid="shortcut-conversations-unread-dot"
                    aria-label={`${totalUnread} unread message${totalUnread === 1 ? "" : "s"}`}
                    className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white"
                  />
                ) : null
              }
            />
            <Shortcut icon={Package} label={t("myaccount.shortcut_my_products", "My Products")} sub={t("myaccount.shortcut_my_products_sub", "Fleet & service hours")} onClick={() => navigate("/compressors")} testId="shortcut-my-products" />
            {serviceTechCan && (
              <Shortcut icon={Wrench} label={t("service_tech.short_label")} sub={t("service_tech.short_sub")} onClick={() => navigate("/service-tech")} testId="shortcut-service-tech" />
            )}
            {serviceTechCan && (
              <Shortcut icon={ShieldWarning} label={t("myaccount.shortcut_claim_warranty", "Claim Warranty")} sub={t("myaccount.shortcut_claim_warranty_sub", "File a warranty claim")} onClick={() => navigate("/warranty/claim")} testId="shortcut-warranty-claim" />
            )}
            <Shortcut icon={Package} label={t("myaccount.shortcut_price_list", "Price List")} sub={t("myaccount.shortcut_price_list_sub", "Browse catalog")} onClick={() => navigate("/us-class")} testId="shortcut-compressors" />
            <Shortcut icon={Sparkle} label={t("myaccount.shortcut_inline_filters", "Inline Filters")} sub={t("myaccount.shortcut_inline_filters_sub", "Drop-in replacements")} onClick={() => navigate("/us-class")} testId="shortcut-filters" />
            <Shortcut icon={Receipt} label={t("myaccount.shortcut_all_quotes", "All Quotes")} sub={t("myaccount.shortcut_all_quotes_sub", "Timeline view")} onClick={() => document.querySelector('[data-testid="my-account-quotes-card"]')?.scrollIntoView({ behavior: 'smooth' })} testId="shortcut-quotes" />
          </div>
        </section>
      </main>

      {openQuoteId && (
        <QuoteDetailModalLoader
          headingId={openQuoteId}
          onClose={() => { setOpenQuoteId(null); handleRefreshQuotes(); }}
          user={user}
        />
      )}
      {showSelfCreate && (
        <AddCustomerModal
          user={user}
          country="US"
          isCA={false}
          selfService={true}
          onClose={() => setShowSelfCreate(false)}
          onCreated={() => {
            setShowSelfCreate(false);
            // Reload the page so the new linked customer record appears.
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm font-mono font-semibold text-slate-900 truncate">{value}</div>
    </div>
  );
}

function Shortcut({ icon: Icon, label, sub, onClick, testId, badge = null }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="relative flex flex-col items-start text-left gap-1 border border-slate-200 rounded-sm p-3 hover:border-blue-400 hover:shadow-sm transition-all bg-white">
      <Icon size={18} weight="bold" className="text-blue-700" />
      <div className="text-sm font-semibold text-slate-900">{label}</div>
      <div className="text-[10px] text-slate-500">{sub}</div>
      {badge}
    </button>
  );
}

// QuoteDetailModal lives in CustomerPage.jsx — import lazily so MyAccountPage stays light.
function QuoteDetailModalLoader({ headingId, onClose, user }) {
  const [Cmp, setCmp] = useState(null);
  useEffect(() => {
    import("@/pages/CustomerPage.jsx").then((mod) => {
      // Named export not exposed; re-export via default module is tricky. Fall back to a light preview.
      setCmp(() => null); // we'll render an inline quote view instead
    });
  }, []);
  // Since QuoteDetailModal isn't exported directly, embed a minimal quote viewer here.
  return <InlineQuoteViewer headingId={headingId} onClose={onClose} user={user} />;
}

function InlineQuoteViewer({ headingId, onClose, user }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filesFor, setFilesFor] = useState(null);
  const audience = user?.role === "customer" ? "customer" : "all";

  useEffect(() => {
    import("@/lib/api").then(({ fetchQuote }) => {
      fetchQuote(headingId).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Failed to load quote")).finally(() => setLoading(false));
    });
  }, [headingId]);

  // Lazy-import PartFilesModal only when we need it
  const [FilesModal, setFilesModal] = useState(null);
  useEffect(() => {
    if (filesFor && !FilesModal) {
      import("@/components/PartFilesModal").then((m) => setFilesModal(() => m.default));
    }
  }, [filesFor, FilesModal]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-6" data-testid="quote-view-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-white rounded-sm shadow-xl border border-slate-200 flex flex-col max-h-[92vh] overflow-hidden">
        <header className="px-4 md:px-6 py-3 border-b border-slate-200 flex items-center gap-3 shrink-0">
          <Receipt size={18} weight="bold" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm md:text-base font-bold font-['Chivo'] text-slate-900 truncate">
              {data?.heading?.QuoteInvoiceNumber || `Quote #${headingId}`}
            </h2>
            <p className="text-[11px] text-slate-500 truncate">
              {fmtDate(data?.heading?.quote_date)} • {data?.heading?.Q_Type || ""} • {data?.heading?.Status || ""}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><SignOut size={16} /></button>
        </header>
        <div className="p-4 md:p-6 overflow-y-auto flex-1">
          {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading…</div>}
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>}
          {!loading && !err && data && (
            <>
              {data.heading?.Shipment && (
                <div className="text-[11px] text-slate-600 mb-3"><b>Shipment:</b> {data.heading.Shipment}</div>
              )}
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_0_rgb(226_232_240)]">
                    <tr className="text-left text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      <th className="px-2 py-2 bg-slate-50">{t("table.product", "Product")}</th>
                      <th className="px-2 py-2 text-right bg-slate-50">{t("table.qty", "Qty")}</th>
                      <th className="px-2 py-2 text-right bg-slate-50">{t("table.price", "Price")}</th>
                      <th className="px-2 py-2 text-right bg-slate-50">{t("myaccount.line_total", "Line Total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.details || []).map((d) => {
                      const qty = Number(d.Quantity) || 0;
                      const price = Number(d.price) || 0;
                      const discount = Number(d.discount) || 0;
                      const lineTotal = qty * price * (1 - discount);
                      return (
                        <tr key={d.QuoteInvoiceDetailID} className="border-b border-slate-100" data-testid={`inline-quote-line-${d.QuoteInvoiceDetailID}`}>
                          <td className="px-2 py-2">
                            <div className="flex items-start gap-2">
                              {d.part_id && (
                                <button onClick={() => setFilesFor({ ID: d.part_id, "Product Name": d.product_name, _ComputedKey: d.product_code })}
                                  data-testid={`inline-quote-files-${d.QuoteInvoiceDetailID}`}
                                  className="mt-0.5 w-5 h-5 inline-flex items-center justify-center rounded-sm border border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 text-slate-500 shrink-0" title={t("myaccount.related_files", "Related files")}>
                                  <FileText size={11} />
                                </button>
                              )}
                              <div className="min-w-0">
                                <div className="font-semibold text-slate-900">{d.product_name || "—"}</div>
                                {d.explanation && <div className="text-[10px] text-slate-500 mt-0.5">{d.explanation}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right font-mono">{qty}</td>
                          <td className="px-2 py-2 text-right font-mono">{fmtMoney(price)}</td>
                          <td className="px-2 py-2 text-right font-mono font-semibold">{fmtMoney(lineTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      {filesFor && FilesModal && <FilesModal part={filesFor} onClose={() => setFilesFor(null)} audience={audience} />}
    </div>
  );
}


// ─── My Uploads Inbox ─────────────────────────────────────────────────
// Shows every file the customer has uploaded across every Part, with a
// one-click "Share with my US Air rep" button that emails the rep a
// downloadable link. The rep is auto-resolved server-side (last quote
// preparer → customer auth email → DEFAULT_REP_EMAIL).
function MyUploadsInbox() {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [shareOpenId, setShareOpenId] = useState(null);
  const [shareNote, setShareNote] = useState("");
  const [shareRepOverride, setShareRepOverride] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [reps, setReps] = useState([]);  // [{email, name, is_suggested}]

  const reload = useCallback(() => {
    setLoading(true);
    fetchMyUploads()
      .then((d) => { setData(d); setShareRepOverride(d?.rep_email || ""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load your uploads"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Lazy-load the reps list once we actually have uploads, so guests &
  // empty inboxes don't pay the network cost.
  useEffect(() => {
    if (!data?.items?.length || reps.length) return;
    fetchMyUploadsReps()
      .then((r) => setReps(r?.items || []))
      .catch(() => {/* non-fatal — input falls back to free-text entry */});
  }, [data, reps.length]);

  // Matched rep name + title for the currently-typed email (if any).
  const matchedRep = useMemo(() => {
    const v = (shareRepOverride || "").trim().toLowerCase();
    if (!v) return null;
    return reps.find((x) => x.email === v) || null;
  }, [shareRepOverride, reps]);
  const matchedRepName = matchedRep?.name || "";
  const matchedRepTitle = matchedRep?.title || "";

  const fileIcon = (mime, isPic) => {
    if (isPic) return <FileImage size={14} weight="bold" className="text-emerald-600 flex-shrink-0" />;
    if ((mime || "").startsWith("video/")) return <FileVideo size={14} weight="bold" className="text-purple-600 flex-shrink-0" />;
    if ((mime || "").includes("pdf")) return <FilePdf size={14} weight="bold" className="text-red-600 flex-shrink-0" />;
    return <FileText size={14} weight="bold" className="text-slate-500 flex-shrink-0" />;
  };

  const onShare = async (fileId) => {
    setSharing(true);
    setShareResult(null);
    try {
      const r = await shareMyUpload(fileId, {
        rep_email_override: (shareRepOverride || "").trim(),
        note: shareNote || "",
      });
      setShareResult({ ok: true, ...r });
      reload();
    } catch (e) {
      setShareResult({
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Share failed",
      });
    } finally {
      setSharing(false);
    }
  };

  return (
    <div data-testid="my-uploads-inbox" className="bg-white border border-slate-200 rounded-sm p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <CloudArrowUp size={16} weight="bold" className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 flex-1">{t("myaccount.my_uploads", "My uploads")}</h2>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">
          {data?.count || 0}
        </span>
      </div>

      {loading && (
        <div className="text-xs text-slate-500 flex items-center gap-2 py-4">
          <Spinner size={12} className="animate-spin" /> Loading your files…
        </div>
      )}
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{err}</div>}
      {!loading && !err && (data?.items || []).length === 0 && (
        <div data-testid="my-uploads-empty" className="text-xs text-slate-500 py-6 text-center">
          {t("myaccount.no_uploads_1", "You haven't uploaded any files yet. Open a Part on the price list and use")} <b>{t("myaccount.pick_file", "Pick file")}</b> {t("myaccount.or_word", "or")}
          <b> {t("myaccount.take_photo", "Take photo")}</b>{t("myaccount.on_mobile_add", " on mobile to add one.")}
        </div>
      )}

      {data && data.items?.length > 0 && (
        <>
          {data.rep_email ? (
            <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5 mb-3 flex items-center gap-1.5 flex-wrap">
              <Sparkle size={11} weight="bold" className="text-blue-700" />
              Your US Air rep:
              <b data-testid="my-uploads-rep-email" className="font-mono text-slate-900">{data.rep_email}</b>
              {data.rep_source === "last_quote_preparer" && (
                <span className="text-slate-400">{t("myaccount.rep_from_last_quote", "· auto-detected from your most recent quote")}</span>
              )}
              {data.rep_source === "default_env" && (
                <span className="text-amber-700">{t("myaccount.rep_fallback", "· using fallback default — feel free to override below")}</span>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5 mb-3 flex items-center gap-1.5">
              <Warning size={11} weight="bold" />
              We couldn't auto-detect a rep email — type one in below before you share.
            </div>
          )}

          <ul className="space-y-2">
            {data.items.map((it) => (
              <li
                key={it.id}
                data-testid={`my-upload-${it.id}`}
                className="border border-slate-200 rounded-sm bg-white"
              >
                <div className="flex items-start gap-3 px-3 py-2.5">
                  {fileIcon(it.mime_type, it.is_picture)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${it.url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-sm font-semibold text-slate-900 truncate hover:text-blue-700 hover:underline"
                      >
                        {it.filename}
                      </a>
                      <span className="text-[10px] font-mono text-slate-400">{Math.round((it.size || 0) / 1024)} KB</span>
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {it.product_name ? <><b>{it.product_name}</b> · </> : null}
                      <span className="font-mono">{it.product_code || `Part ${it.part_id}`}</span>
                      {it.product_class ? ` · ${it.product_class}` : ""}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Uploaded {fmtDate(it.uploaded_at)}
                      {it.shared_at && (
                        <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle size={9} weight="bold" /> Shared with {it.shared_with}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`my-upload-share-${it.id}`}
                    onClick={() => {
                      setShareOpenId(shareOpenId === it.id ? null : it.id);
                      setShareNote("");
                      setShareResult(null);
                    }}
                    className="inline-flex items-center gap-1 h-7 px-2.5 text-[10px] font-bold rounded-sm bg-blue-700 text-white hover:bg-blue-800 whitespace-nowrap shrink-0"
                  >
                    <PaperPlaneTilt size={10} weight="bold" />
                    {shareOpenId === it.id ? "Cancel" : "Share with rep"}
                  </button>
                </div>
                {shareOpenId === it.id && (
                  <div data-testid={`my-upload-share-form-${it.id}`} className="border-t border-slate-100 px-3 py-2.5 bg-slate-50/40 space-y-2">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Send to</label>
                      <input
                        data-testid={`my-upload-share-email-${it.id}`}
                        type="email"
                        list={`my-upload-share-reps-${it.id}`}
                        autoComplete="off"
                        value={shareRepOverride}
                        onChange={(e) => setShareRepOverride(e.target.value)}
                        placeholder="rep@usaircompressor.com"
                        className="w-full h-8 px-2 text-xs rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 font-mono"
                      />
                      {reps.length > 0 && (
                        <datalist id={`my-upload-share-reps-${it.id}`} data-testid={`my-upload-share-reps-list-${it.id}`}>
                          {reps.map((r) => (
                            <option key={r.email} value={r.email}>
                              {r.name}
                              {r.title ? ` · ${r.title}` : ""}
                              {r.is_suggested ? " (your rep)" : ""}
                            </option>
                          ))}
                        </datalist>
                      )}
                      {matchedRepName && (
                        <div
                          data-testid={`my-upload-share-rep-match-${it.id}`}
                          className="text-[10px] text-emerald-700 mt-1 inline-flex items-center gap-1"
                        >
                          <CheckCircle size={10} weight="bold" />
                          {matchedRepName}
                          {matchedRepTitle ? <span className="text-slate-400"> · {matchedRepTitle}</span> : null}
                        </div>
                      )}
                      {reps.length > 0 && !matchedRepName && (shareRepOverride || "").trim() && (
                        <div className="text-[10px] text-slate-400 mt-1">
                          Not in our directory — we'll still send to this address.
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("myaccount.note_optional", "Note (optional)")}</label>
                      <textarea
                        data-testid={`my-upload-share-note-${it.id}`}
                        value={shareNote}
                        onChange={(e) => setShareNote(e.target.value)}
                        rows={2}
                        placeholder={t("myaccount.note_placeholder", "What should we look at? Damage, missing parts, photo for confirmation, etc.")}
                        className="w-full px-2 py-1.5 text-xs rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 resize-none"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`my-upload-share-send-${it.id}`}
                        onClick={() => onShare(it.id)}
                        disabled={sharing || !(shareRepOverride || "").trim()}
                        className="inline-flex items-center gap-1 h-7 px-2.5 text-[10px] font-bold rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-slate-300 whitespace-nowrap"
                      >
                        {sharing ? (
                          <><Spinner size={10} className="animate-spin" /> Sending…</>
                        ) : (
                          <><PaperPlaneTilt size={10} weight="bold" /> Send</>
                        )}
                      </button>
                      {shareResult?.ok && shareResult.filename === it.filename && (
                        <span data-testid={`my-upload-share-ok-${it.id}`} className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-1">
                          <CheckCircle size={11} weight="bold" />
                          Sent to {shareResult.rep_email}{shareResult.send_result?.status === "delivered" ? "" : " (queued)"}
                        </span>
                      )}
                      {shareResult && !shareResult.ok && (
                        <span data-testid={`my-upload-share-err-${it.id}`} className="text-[10px] font-bold text-red-700 inline-flex items-center gap-1">
                          <Warning size={11} weight="bold" /> {shareResult.error}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
